import { getCapacityUnit } from 'utils/configUtilities';
import { formatCapacity, formatKw } from 'shared/lib/format';
import type { FlatConfig } from 'domain/index';
import type { Metrics, Vendor } from 'shared/state/types';

export type SlideFigure = { label: string; value: string };
export type SlidePreviewModel = { title: string; subtitle: string; figures: SlideFigure[] };

/** Pure model behind the preview. Mirrors what rackSlidePPTGenerator draws —
 *  system shape and capacity, never pricing.
 *
 *  The vendor param is narrowed to just the field this function reads. The real
 *  config/vendorRegistry.js getVendor() (a JS module with a JSDoc-typed return) and the
 *  full shared/state/types Vendor both structurally satisfy this; typing the parameter
 *  as the full `Vendor` interface does not, because vendorRegistry.js's VendorConfig
 *  JSDoc typedef never lists businessRules.registrationMultiplier even though every
 *  vendor entry sets it at runtime — a pre-existing gap between the JS registry and the
 *  TS Vendor type, not something this feature should paper over by casting at the call
 *  site instead. */
export function buildSlidePreviewModel(config: FlatConfig, metrics: Metrics | null, vendor: Pick<Vendor, 'displayName'>): SlidePreviewModel {
  // Fix round 1, ruling R13: rackSlidePPTGenerator.js:483-485 gives each of the three
  // capacity figures a DIFFERENT unit suffix — capUnitR = getCapacityUnit(isPBUnit, 'r')
  // for RAW, capUnit = getCapacityUnit(isPBUnit) (no suffix) for Useable, capUnitE =
  // getCapacityUnit(isPBUnit, 'e') for Effective/compressed. A flat 'TB'/'TiB' on all
  // three doesn't match what the deck actually prints.
  const isPBUnit = Boolean(config.isPBUnit);
  const rawUnit = getCapacityUnit(isPBUnit, 'r');
  const usableUnit = getCapacityUnit(isPBUnit);
  const effectiveUnit = getCapacityUnit(isPBUnit, 'e');
  const num = (k: string) => Number(metrics?.[k] ?? 0);

  // W7: every hardware row in the deck gates on a POSITIVE COUNT as well as its enable
  // flag — buildD1NHardwareSummaryRows `if (config.veloCount > 0)`,
  // buildAFAHardwareSummaryRows `if (config.afaEnabled && config.afaNodeCount > 0)`,
  // buildHybridHardwareSummaryRows `if (config.hybridEnabled && config.hybridNodeCount >
  // 0)` (rackSlidePPTGenerator.js:261, :274, :286). The D row was emitted
  // unconditionally and F/S gated on `enabled` alone, so a 0-director config previewed
  // "0 D" where the deck omits the row entirely.
  const dNodes = Number(config.veloCount ?? 0);
  const fNodes = Number(config.afaNodeCount ?? 0);
  const sNodes = Number(config.hybridNodeCount ?? 0);
  const nodeRows = [
    dNodes > 0 ? `${dNodes} D` : null,
    config.afaEnabled && fNodes > 0 ? `${fNodes} F` : null,
    config.hybridEnabled && sNodes > 0 ? `${sNodes} S` : null,
  ].filter(Boolean);
  // The deck drops the rows and says nothing; this preview's six-figure grid is fixed
  // (its label set is pinned by a plan-mandated test), so the figure still has to render.
  // An empty value would show a labelled tile with nothing in it — indistinguishable from
  // a figure that failed to compute — so fall back to the em dash shared/lib/format's
  // formatPerTb already uses as this repo's "no value" glyph.
  const nodes = nodeRows.length > 0 ? nodeRows.join(' · ') : '—';

  // Controller ruling P11: formatKw returns the STRING 'N/A' (not a number) when the
  // metric is 0 or absent. Appending ' kW' unconditionally renders the nonsense
  // "N/A kW" — proven by a failing test run against this exact bug before this guard
  // was added. Only attach the unit when there's a real figure to attach it to.
  const power = formatKw(num('totalNominalPower'));
  const powerValue = power === 'N/A' ? power : `${power} kW`;

  return {
    title: String(config.preparedFor || 'VDURA V5000'),
    subtitle: String(config.quoteDescription || vendor.displayName),
    figures: [
      { label: 'Nodes', value: nodes },
      { label: 'RAW Capacity', value: `${formatCapacity(num('totalRawCapacity'))} ${rawUnit}` },
      { label: 'Useable Capacity', value: `${formatCapacity(num('totalEffectiveCapacity'))} ${usableUnit}` },
      { label: 'Effective Capacity', value: `${formatCapacity(num('totalCompressedEffectiveCapacity'))} ${effectiveUnit}` },
      // W6: the deck prints `Rack Units / Racks` as
      // `${fmt(metrics.totalRU)} RU / ${Math.ceil(metrics.totalRU / 42)}`
      // (rackSlidePPTGenerator.js:517-518). This matches that VALUE; the LABEL stays
      // 'Rack Units' — the six labels are pinned by a plan-mandated test, and the preview
      // is a summary of the deck, not a transcription of it. formatCapacity is the shared
      // thousands-separated formatter (its ambient-locale-vs-the-deck's-'en-US' gap is
      // ruled a repo-wide question, out of scope here). The 42 is the deck's own literal,
      // not config.rackSize — the deck divides by 42 whatever rack the user picked.
      { label: 'Rack Units', value: `${formatCapacity(num('totalRU'))} RU / ${Math.ceil(num('totalRU') / 42)}` },
      { label: 'Typical Power', value: powerValue },
    ],
  };
}
